import I18n from 'i18n.js.erb'
import React from 'react'
import { formatLocalizedDate, getWorkingStationMetaName, isDefined } from '../../utils/utils'
import IconTooltip from './IconTooltip'

const STATE_DONE = 'done'
const STATE_CURRENT = 'doing'
const STATE_FUTURE = 'future'
// Stati con cui il PMS marca una stazione come conclusa per l'ordine
const COMPLETED_STEP_STATES = ['done', 'fulfilled']
// Ordine interrotto dopo l'ultima tappa: non c'e' un collegamento successivo da spezzare,
// quindi l'icona di recesso torna sulla stazione finale
const STATE_REJECTED = 'rejected'
// Tappa di arresto del ramo recesso: il pallino rosso su cui la timeline devia
const STATE_WITHDRAWAL_STOP = 'withdrawal-stop'

// Una tappa e' stata attraversata solo se l'ordine ha davvero un processing step su quella stazione:
// il tracking non e' una linea continua, gli ordini interrotti saltano le stazioni che non hanno fatto
const isReached = (processingStep) => isDefined(processingStep.id)
// L'annullamento chiude con 'fulfilled' anche la stazione su cui l'ordine era fermo, senza pero' datarla:
// solo il done_at distingue una stazione davvero conclusa da una chiusa d'ufficio dal recesso
const isCompleted = (processingStep) =>
  isReached(processingStep) && COMPLETED_STEP_STATES.includes(processingStep.state) && isDefined(processingStep.done_at)

const TrackerStep = ({ step, index }) => {
  const position = index % 2 === 0 ? 'above' : 'below'
  // Ordine interrotto (rifiutato / annullato): l'icona di recesso spezza il collegamento che entra in questa
  // tappa, perché il flusso si è fermato tra la tappa precedente e questa, non su una stazione
  const interrupted = step.interrupted === true
  // Col recesso l'ordine visivo delle tappe non coincide piu' con quello del DOM, quindi il colore del
  // collegamento in ingresso non puo' dipendere dai sibling CSS: lo decide il componente
  const link = isDefined(step.link) ? `order-tracking__step--link-${step.link}` : ''

  return (
    <li
      className={`order-tracking__step order-tracking__step--${step.state} order-tracking__step--${position} ${link} ${interrupted ? 'order-tracking__step--interrupted' : ''}`}
      style={isDefined(step.column) ? { '--track-col': step.column } : undefined}
    >
      <span className="order-tracking__meta-name">{step.metaName.toUpperCase()}</span>

      <div className="order-tracking__bar">
        {interrupted &&
          <span className="order-tracking__break">
            <img className="order-tracking__break-icon" src="/images/icons/tracking_withdrawal.svg" aria-hidden="true" />
          </span>
        }

        <div className="order-tracking__dot">
          {step.state === STATE_DONE && <img className="order-tracking__icon" src="/images/icons/white/tracking_checkmark.svg" aria-hidden="true" />}
          {step.state === STATE_CURRENT && <img className="order-tracking__icon" src="/images/icons/white/arrow-right.svg" aria-hidden="true" />}
          {step.state === STATE_REJECTED && <img className="order-tracking__icon order-tracking__icon--withdrawal" src="/images/icons/tracking_withdrawal.svg" aria-hidden="true" />}
          {step.state === STATE_WITHDRAWAL_STOP && <img className="order-tracking__icon order-tracking__icon--withdrawal" src="/images/icons/tracking_withdrawal.svg" aria-hidden="true" />}
        </div>
      </div>

      {isDefined(step.date) && <span className="order-tracking__date">{formatLocalizedDate(step.date)}</span>}
    </li>
  )
}

// Curva del ramo recesso: i path sono quelli disegnati nel Figma (nodi 1:936 e 1:954),
// normalizzati sul viewBox e stirati sul riquadro tra i due pallini. Ogni curva porta due
// orientamenti della stessa forma: quello orizzontale per la timeline desktop e il suo
// trasposto per la corsia verticale del mobile; il CSS mostra quello del breakpoint.
// Il pathLength normalizzato tiene il tratteggio uniforme qualunque sia la misura del riquadro.
// La curva tocca i bordi del riquadro: senza margine meta' dello spessore finisce fuori e viene
// tagliata, e la linea si legge a meta' spessore vicino ai pallini. Il viewBox tiene quel margine
// ($order-tracking-curve-pad nel CSS, che gonfia il riquadro della stessa misura)
const BRANCH_CURVE_PATHS = {
  entry: {
    desktop: 'M0 0 C 52.65 0, 52.65 10.28, 52.65 48.6 C 52.65 86.9, 71.84 100, 100 100',
    mobile: 'M0 0 C 0 52.65, 10.28 52.65, 48.6 52.65 C 86.9 52.65, 100 71.84, 100 100',
  },
  exit: {
    desktop: 'M100 0 C 47.35 0, 47.35 10.28, 47.35 48.6 C 47.35 86.93, 28.16 100, 0 100',
    mobile: 'M100 0 C 100 52.65, 89.72 52.65, 51.4 52.65 C 13.1 52.65, 0 71.84, 0 100',
  },
}

const BranchCurve = ({ variant, link }) => (
  <svg
    className={`order-tracking__branch-curve order-tracking__branch-curve--${variant} ${isDefined(link) ? `order-tracking__branch-curve--${link}` : ''}`}
    viewBox="-6 -6 112 112"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    {['desktop', 'mobile'].map((layout) =>
      <path
        key={layout}
        className={`order-tracking__branch-curve-path order-tracking__branch-curve-path--${layout}`}
        d={BRANCH_CURVE_PATHS[variant][layout]}
        pathLength="100"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    )}
  </svg>
)

// Ramo del recesso: sul desktop scende dalla timeline col pallino rosso di annullamento, attraversa
// approvazione e rimborso e risale sugli step di rispedizione; sul mobile le sue tappe scorrono in
// colonna come tutte le altre (il contenitore diventa display: contents)
const WithdrawalBranch = ({ branchSteps, columnStart, columnEnd, rejoinLink }) => (
  <li
    className="order-tracking__branch"
    style={{ '--branch-start': columnStart, '--branch-end': columnEnd, '--branch-span': columnEnd - columnStart }}
  >
    <BranchCurve variant="entry" link={null} />

    <ol className="order-tracking__branch-steps">
      {branchSteps.map((step, index) => <TrackerStep key={step.key} step={step} index={index + 1} />)}
    </ol>

    {isDefined(rejoinLink) && <BranchCurve variant="exit" link={rejoinLink} />}
  </li>
)

const TrackingHeader = ({ submissionList, firstStepDone, showDates }) => {
  const estimatedDate = submissionList.tracking.completion_estimates?.estimated_completion_date
  const realisticDate = submissionList.tracking.completion_estimates?.realistic_estimated_completion_date
  const closingDate = submissionList.closing_date
  const isDelivered = isDefined(closingDate)
  const isFaster = isDefined(realisticDate) && new Date(realisticDate) < new Date(estimatedDate)

  return (
    <header className="order-tracking__header">
      <h2 className="order-tracking__title text-aig-gradient">
        {I18n.t('js.common.submissionListTracking.title')}
      </h2>

      {/* la data di consegna avvenuta la vedono tutti, quella prevista solo i mirai tester */}
      {(isDelivered || (showDates && isDefined(estimatedDate))) &&
        <div className="order-tracking__delivery">
          <span className="order-tracking__delivery-prefix">
            {isDelivered ? I18n.t('js.common.submissionListTracking.deliveryDoneAt') : I18n.t('js.common.submissionListTracking.deliveryPlannedOn')}
          </span>
          <span className="order-tracking__delivery-date">
            {firstStepDone ?
              <React.Fragment>
                {formatLocalizedDate(isDelivered ? closingDate : estimatedDate)}
                {isFaster && !isDelivered &&
                  <IconTooltip
                    tooltipContent={I18n.t('js.common.submissionListTracking.fasterCompletion')}
                    customComponentToRender={<i className="fas fa-tachometer-alt fa-sm ml-2" />}
                    customComponentToRenderCssClass="d-inline-block align-middle"
                  />
                }
              </React.Fragment>
              :
              I18n.t('js.common.submissionListTracking.trackingAvailableAfterAcceptance')
            }
          </span>
        </div>
      }
    </header>
  )
}

// showHeader a false toglie titolo e data di consegna: il backoffice mostra solo la timeline.
// Conferma e stazioni concluse portano sempre il giorno in cui si sono concluse; showCompletedDates lo
// aggiunge anche sotto le tappe raggiunte del recesso
const SubmissionListTracking = ({ submissionList, showHeader = true, showCompletedDates = false }) => {
  const processingSteps = submissionList.tracking.processing_steps
  const unfulfilled = submissionList.tracking.completion_estimates?.unfulfilled_working_stations
  // le date previste sono riservate ai mirai tester, il tracking pubblico mostra solo quelle concluse
  const showDates = submissionList.tracking.show_dates === true
  // status sintetico lato server: 'failed' per gli ordini rifiutati o annullati
  const isFailed = submissionList.tracking.status === 'failed'
  const firstStepDone = COMPLETED_STEP_STATES.includes(processingSteps[0]?.state)

  // Recesso in corso o accolto: la timeline devia sul ramo dedicato invece di mostrare la x di
  // interruzione. Gli annullamenti senza richiesta di recesso restano sulla resa classica
  const withdrawal = submissionList.tracking.withdrawal
  const hasWithdrawal = isDefined(withdrawal)
  const resumptionWorkingStationIds = withdrawal?.resumption_working_station_ids ?? []
  // Step nati con l'annullamento: la rispedizione del pacco, su cui il ramo del recesso si ricongiunge
  const isResumptionStep = (processingStep) => resumptionWorkingStationIds.includes(processingStep.working_station.id)

  const isDelivered = !isFailed && !hasWithdrawal && isDefined(submissionList.closing_date)

  // Stazione su cui la lavorazione si e' fermata, decisa dal server: la x del recesso va li' sopra,
  // altrimenti quella fase sembrerebbe superata correttamente
  const interruptedWorkingStationId = submissionList.tracking.interrupted_working_station_id
  const interruptedStepIndex = isFailed && !hasWithdrawal && isDefined(interruptedWorkingStationId)
    ? processingSteps.findIndex((processingStep) => processingStep.working_station.id === interruptedWorkingStationId)
    : -1

  // L'ordine si e' fermato dopo l'ultima stazione che ha davvero concluso
  const lastCompletedIndex = processingSteps.findLastIndex(isCompleted)

  // Ordine interrotto quando ogni working station era già stata completata: oltre l'ultima non
  // c'è un collegamento da spezzare, quindi la x resta sulla stazione finale
  const interruptedAtLastStep = isFailed && !hasWithdrawal && interruptedStepIndex === -1 && processingSteps.length > 0 && lastCompletedIndex === processingSteps.length - 1

  const resolveState = (processingStep, index) => {
    if (hasWithdrawal) {
      if (!isReached(processingStep)) return STATE_FUTURE
      if (isResumptionStep(processingStep)) return isCompleted(processingStep) ? STATE_DONE : STATE_CURRENT
      // La stazione su cui l'ordine era fermo viene chiusa d'ufficio dal recesso: non e' un
      // avanzamento, resta grigia come quelle mai attraversate
      return isCompleted(processingStep) ? STATE_DONE : STATE_FUTURE
    }
    if (isDelivered) return STATE_DONE
    if (index === interruptedStepIndex) return STATE_REJECTED
    // Oltre il punto di interruzione la lavorazione riprende sul confezionamento, che rispedisce il
    // pacco al cliente: quella stazione resta viva, mentre quelle mai attraversate restano future
    if (!isReached(processingStep)) return STATE_FUTURE
    if (!isCompleted(processingStep)) return STATE_CURRENT
    return interruptedAtLastStep && index === lastCompletedIndex ? STATE_REJECTED : STATE_DONE
  }

  // Indice nella timeline (la conferma occupa lo 0) della tappa in cui entra il collegamento spezzato:
  // e' quella subito dopo l'ultima conclusa, così la x cade tra le due. Serve solo quando l'ordine si e'
  // fermato tra due stazioni, senza averne mai aperta una
  const interruptedIndex = isFailed && !hasWithdrawal && interruptedStepIndex === -1 && !interruptedAtLastStep ? lastCompletedIndex + 2 : -1

  // Un ordine tracciato è sempre già confermato: la prima tappa è done anche prima che
  // una working station sia iniziata, e la freccia compare solo da lì in poi
  const confirmStep = {
    key: 'confirmed_working_station',
    metaName: I18n.t('js.common.submissionListTracking.confirmStepMetaName'),
    date: submissionList.confirmation_date,
    state: STATE_DONE,
  }

  // Il ramo del recesso parte dall'ultima stazione conclusa dalla lavorazione vera (indice di
  // timeline: la conferma occupa lo 0) e si ricongiunge sulla prima stazione di rispedizione
  const lastProductionIndex = processingSteps.findLastIndex((processingStep) => !isResumptionStep(processingStep) && isCompleted(processingStep))
  const branchAfterIndex = lastProductionIndex + 1
  const firstResumptionIndex = processingSteps.findIndex(isResumptionStep)
  const rejoinIndex = firstResumptionIndex === -1 ? -1 : firstResumptionIndex + 1
  const timelineColumns = processingSteps.length + 1

  const resolveLink = (timelineIndex, state, previousState) => {
    if (!hasWithdrawal || timelineIndex <= branchAfterIndex) return null
    // La prima stazione di rispedizione riceve il flusso dalla curva di ricongiungimento, non
    // dalla linea principale: il collegamento orizzontale resta quello grigio del salto
    if (timelineIndex === rejoinIndex) return 'dashed'
    const reached = [STATE_DONE, STATE_CURRENT].includes(state)
    const previousReached = [STATE_DONE, STATE_CURRENT].includes(previousState)
    return reached && previousReached ? 'solid' : 'dashed'
  }

  // Lo stato della richiesta e' gia' la decisione del server: accepted implica l'approvazione,
  // refunded implica anche il rimborso erogato
  const withdrawalAccepted = ['accepted', 'refunded'].includes(withdrawal?.state)
  const withdrawalRefunded = withdrawal?.state === 'refunded'
  const branchSteps = hasWithdrawal ? [
    // Date sotto le tappe del ramo solo con showCompletedDates: il Figma del cliente non le prevede, e la
    // pagina le mostra gia' nel riepilogo della richiesta di recesso sopra al tracking
    {
      key: 'withdrawal_stop',
      metaName: I18n.t('js.common.submissionListTracking.withdrawalStopMetaName'),
      date: showCompletedDates ? withdrawal.requested_at : null,
      state: STATE_WITHDRAWAL_STOP,
      link: 'solid',
    },
    {
      key: 'withdrawal_approval',
      metaName: I18n.t('js.common.submissionListTracking.withdrawalApprovalMetaName'),
      date: showCompletedDates && withdrawalAccepted ? withdrawal.accepted_at : null,
      state: withdrawalAccepted ? STATE_DONE : STATE_FUTURE,
      link: withdrawalAccepted ? 'solid' : 'dashed',
    },
    {
      key: 'withdrawal_refund',
      metaName: I18n.t('js.common.submissionListTracking.withdrawalRefundMetaName'),
      date: showCompletedDates && withdrawalRefunded ? withdrawal.refunded_at : null,
      state: withdrawalRefunded ? STATE_DONE : STATE_FUTURE,
      link: withdrawalRefunded ? 'solid' : 'dashed',
    },
  ] : []

  // La curva di ricongiungimento diventa blu quando la rispedizione e' davvero partita
  const rejoinLink = rejoinIndex === -1 ? null : (isReached(processingSteps[firstResumptionIndex]) ? 'solid' : 'dashed')

  let previousState = confirmStep.state
  return (
    <div className="order-tracking w-100">
      {showHeader && <TrackingHeader submissionList={submissionList} firstStepDone={firstStepDone} showDates={showDates} />}

      <ol
        className={`order-tracking__timeline ${hasWithdrawal ? 'order-tracking__timeline--withdrawal' : ''}`}
        style={hasWithdrawal ? { '--track-cols': timelineColumns } : undefined}
      >
        <TrackerStep key={confirmStep.key} step={hasWithdrawal ? { ...confirmStep, column: 1 } : confirmStep} index={0} />
        {hasWithdrawal && branchAfterIndex === 0 &&
          <WithdrawalBranch branchSteps={branchSteps} columnStart={1} columnEnd={rejoinIndex === -1 ? timelineColumns + 1 : rejoinIndex + 2} rejoinLink={rejoinLink} />
        }
        {
          processingSteps.map((processingStep, index) => {
            const timelineIndex = index + 1
            const state = resolveState(processingStep, index)
            const link = resolveLink(timelineIndex, state, previousState)
            previousState = state
            const estimatedCompletionDate = unfulfilled?.[processingStep.working_station.id]?.estimated_completion_date
            const step = {
              key: processingStep.working_station.id,
              metaName: getWorkingStationMetaName(processingStep.working_station),
              date: state === STATE_DONE && isCompleted(processingStep) ?
                      processingStep.done_at
                    : showDates && state === STATE_FUTURE ? estimatedCompletionDate : null,
              state,
              link,
              column: hasWithdrawal ? timelineIndex + 1 : null,
            }
            return (
              <React.Fragment key={step.key}>
                <TrackerStep step={{ ...step, interrupted: timelineIndex === interruptedIndex }} index={timelineIndex} />
                {hasWithdrawal && branchAfterIndex === timelineIndex &&
                  <WithdrawalBranch branchSteps={branchSteps} columnStart={timelineIndex + 1} columnEnd={rejoinIndex === -1 ? timelineColumns + 1 : rejoinIndex + 2} rejoinLink={rejoinLink} />
                }
              </React.Fragment>
            )
          })
        }
      </ol>
    </div>
  )
}

export default SubmissionListTracking
